import { Mars, NonBinary, Venus } from "lucide-react";

import type { Company, Contact, ContactGender } from "../types";

export const defaultEmailJsonb = [{ email: null, type: null }];
export const defaultPhoneJsonb = [{ number: null, type: null }];

const cleanContactArrayFields = (data: Contact) => {
  const cleanedEmailJsonb =
    data.email_jsonb?.filter((e) => e.email != null) || [];
  const cleanedPhoneJsonb =
    data.phone_jsonb?.filter((p) => p.number != null) || [];
  return {
    ...data,
    phone_jsonb: cleanedPhoneJsonb.length > 0 ? cleanedPhoneJsonb : null,
    email_jsonb: cleanedEmailJsonb.length > 0 ? cleanedEmailJsonb : null,
  };
};

export const cleanupContactForCreate = (data: Contact) => {
  return cleanContactArrayFields({
    ...data,
    first_seen: new Date().toISOString(),
    last_seen: new Date().toISOString(),
    tags: [],
  });
};

export const cleanupContactForEdit = cleanContactArrayFields;

/** First-touch SLA windows (minutes since first_seen while still untouched). */
export const LEAD_SLA_WARN_MINUTES = 15;
export const LEAD_SLA_OVERDUE_MINUTES = 60;
/** Activity within this window of first_seen counts as ingest-only, not agent touch. */
export const LEAD_UNTOUCHED_MAX_MS = 5 * 60 * 1000;
export const LEAD_STALE_HOURS = 48;

export type LeadSlaLevel = "ok" | "warn" | "overdue";

export function isLeadUntouched(contact: {
  first_seen?: string | null;
  last_seen?: string | null;
}): boolean {
  if (!contact.first_seen) return false;
  const first = new Date(contact.first_seen).getTime();
  const last = new Date(contact.last_seen || contact.first_seen).getTime();
  return last - first <= LEAD_UNTOUCHED_MAX_MS;
}

/** Minutes from first_seen → last_seen once the lead is no longer untouched; null if still untouched or timestamps missing. */
export function getFirstTouchMinutes(contact: {
  first_seen?: string | null;
  last_seen?: string | null;
}): number | null {
  if (isLeadUntouched(contact)) return null;
  if (!contact.first_seen || !contact.last_seen) return null;
  const first = new Date(contact.first_seen).getTime();
  const last = new Date(contact.last_seen).getTime();
  if (Number.isNaN(first) || Number.isNaN(last)) return null;
  const minutes = (last - first) / 60_000;
  if (minutes < 0) return null;
  return minutes;
}

/** Minutes from first_seen → earliest outbound agent activity; null when either timestamp is missing. */
export function getFirstTouchMinutesFromActivity(
  first_seen?: string | null,
  earliestActivityIso?: string | null,
): number | null {
  if (!first_seen || !earliestActivityIso) return null;
  const first = new Date(first_seen).getTime();
  const activity = new Date(earliestActivityIso).getTime();
  if (Number.isNaN(first) || Number.isNaN(activity)) return null;
  const minutes = (activity - first) / 60_000;
  if (minutes < 0) return null;
  return minutes;
}

/** Null when the lead already has agent activity past the ingest window. */
export function getLeadSlaLevel(
  contact: {
    first_seen?: string | null;
    last_seen?: string | null;
  },
  now = Date.now(),
): LeadSlaLevel | null {
  if (!contact.first_seen || !isLeadUntouched(contact)) return null;
  const ageMin = (now - new Date(contact.first_seen).getTime()) / 60_000;
  if (ageMin >= LEAD_SLA_OVERDUE_MINUTES) return "overdue";
  if (ageMin >= LEAD_SLA_WARN_MINUTES) return "warn";
  return "ok";
}

/** Stable URL tokens for desk queues (timestamps / nulls expand at query time). */
export type DeskQueue =
  | "unassigned"
  | "sla_overdue"
  | "stale_48h"
  | "needs_whatsapp_reply";

export function leadSlaFilterValues(now = new Date()) {
  // Keep `now` for callers that still expand timestamps locally.
  void now;
  return {
    /**
     * Stable desk-queue tokens for the URL / ToggleFilterButton selection.
     * Expanded to real PostgREST predicates in the dataProvider (see
     * `expandDeskQueueFilter`). Avoid raw `null` in the URL — ra rehydrates it
     * as the string "null", which broke the Unassigned active/X state.
     */
    unassigned: { desk_queue: "unassigned" as const },
    slaOverdue: { desk_queue: "sla_overdue" as const },
    stale: { desk_queue: "stale_48h" as const },
  };
}

/** Real PostgREST predicates for a desk queue at query time. */
export function deskQueueToFilter(
  queue: DeskQueue,
  now = new Date(),
): Record<string, unknown> {
  const warnCutoff = new Date(
    now.getTime() - LEAD_SLA_WARN_MINUTES * 60_000,
  ).toISOString();
  const activityCap = new Date(
    now.getTime() - LEAD_UNTOUCHED_MAX_MS,
  ).toISOString();
  const staleCutoff = new Date(
    now.getTime() - LEAD_STALE_HOURS * 60 * 60_000,
  ).toISOString();

  switch (queue) {
    case "unassigned":
      return { "sales_id@is": null };
    case "sla_overdue":
      return {
        "first_seen@lte": warnCutoff,
        "last_seen@lte": activityCap,
      };
    case "stale_48h":
      return { "last_seen@lte": staleCutoff };
    case "needs_whatsapp_reply":
      // Expanded asynchronously in the dataProvider (needs WA message lookup).
      return {};
    default:
      return {};
  }
}

/**
 * Replace a stable `desk_queue` token with real filter predicates.
 * Leaves other filter keys untouched. Returns a new object.
 */
export function expandDeskQueueFilter(
  filter: Record<string, unknown> | undefined,
  now = new Date(),
): Record<string, unknown> {
  if (!filter || filter.desk_queue == null) {
    return { ...(filter ?? {}) };
  }
  const queue = String(filter.desk_queue) as DeskQueue;
  const { desk_queue: _drop, ...rest } = filter;
  return { ...rest, ...deskQueueToFilter(queue, now) };
}

export type ContactTimelineKind =
  | "note"
  | "call"
  | "whatsapp"
  | "visit"
  | "portal";

export type ContactTimelineItem = {
  id: string;
  kind: ContactTimelineKind;
  at: string;
  title: string;
  body?: string;
  meta?: string;
  /** WhatsApp delivery/read status (e.g. unread) when kind === "whatsapp". */
  status?: string;
};

export function sortTimelineItemsDesc(
  items: ContactTimelineItem[],
): ContactTimelineItem[] {
  return [...items].sort(
    (a, b) => new Date(b.at).getTime() - new Date(a.at).getTime(),
  );
}

/** Wave 9 lead assists — deterministic rules engine (not ML / no network). */
export type LeadAssistAction = "call" | "whatsapp" | "schedule_visit" | "none";

export type LeadNextAction = {
  action: LeadAssistAction;
  label: string;
  reason: string;
};

export type LeadScoreTier = "hot" | "warm" | "cold";

export type LeadScore = {
  score: number;
  tier: LeadScoreTier;
  factors: string[];
};

export type LeadAssistInput = {
  contact: {
    first_name?: string | null;
    last_name?: string | null;
    first_seen?: string | null;
    last_seen?: string | null;
    budget_min?: number | null;
    budget_max?: number | null;
    preferred_location?: string | null;
    preferred_type?: string | null;
    lead_source?: string | null;
    status?: string | null;
    phone_jsonb?: { number?: string }[] | null;
  };
  timeline?: ContactTimelineItem[];
  needsWhatsAppReply?: boolean;
  hasCompletedVisit?: boolean;
  hasUpcomingVisit?: boolean;
  lastInboundWhatsAppBody?: string | null;
};

const MEANINGFUL_TIMELINE_KINDS = new Set<ContactTimelineKind>([
  "visit",
  "call",
  "whatsapp",
  "portal",
]);

const LEAD_SOURCE_LABELS: Record<string, string> = {
  Meta_Ads: "Meta Ads",
  Google_Ads: "Google Ads",
  Walk_In: "Walk-in",
  Channel_Partner: "Channel partner",
  "99acres": "99acres",
  MagicBricks: "MagicBricks",
  "Housing.com": "Housing.com",
  NoBroker: "NoBroker",
  Website: "Website",
};

function formatCompactInr(val: number): string {
  if (val >= 10_000_000) {
    const cr = val / 10_000_000;
    const rounded = Math.round(cr * 10) / 10;
    return `₹${Number.isInteger(rounded) ? rounded.toFixed(0) : rounded}Cr`;
  }
  if (val >= 100_000) {
    const lakhs = val / 100_000;
    const rounded = Math.round(lakhs * 10) / 10;
    return `₹${Number.isInteger(rounded) ? rounded.toFixed(0) : rounded}L`;
  }
  return `₹${val.toLocaleString("en-IN")}`;
}

function formatBudgetBand(
  budgetMin?: number | null,
  budgetMax?: number | null,
): string | null {
  const minStr =
    budgetMin != null && !Number.isNaN(budgetMin)
      ? formatCompactInr(budgetMin)
      : "";
  const maxStr =
    budgetMax != null && !Number.isNaN(budgetMax)
      ? formatCompactInr(budgetMax)
      : "";
  if (minStr && maxStr) return `${minStr}–${maxStr}`;
  if (minStr || maxStr) return minStr || maxStr;
  return null;
}

function formatLeadSourceLabel(source?: string | null): string {
  if (!source?.trim()) return "Lead";
  const label = LEAD_SOURCE_LABELS[source] ?? source.replace(/_/g, " ").trim();
  return `${label} lead`;
}

function formatRelativeTime(iso: string, now = Date.now()): string {
  const ms = now - new Date(iso).getTime();
  if (Number.isNaN(ms) || ms < 60_000) return "just now";
  const mins = Math.floor(ms / 60_000);
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return `${Math.floor(days / 7)}w ago`;
}

function getLatestMeaningfulTimelineItem(
  timeline: ContactTimelineItem[],
): ContactTimelineItem | undefined {
  return sortTimelineItemsDesc(
    timeline.filter((item) => MEANINGFUL_TIMELINE_KINDS.has(item.kind)),
  )[0];
}

function formatTimelineSnippet(
  item: ContactTimelineItem,
  now = Date.now(),
): string {
  const rel = formatRelativeTime(item.at, now);
  if (item.kind === "whatsapp") {
    const dir = item.meta?.toUpperCase();
    if (dir === "INBOUND") return `inbound WA ${rel}`;
    return `WA ${rel}`;
  }
  if (item.kind === "call") return `call ${rel}`;
  if (item.kind === "visit") return `visit ${rel}`;
  if (item.kind === "portal") return `portal ${rel}`;
  return `${item.kind} ${rel}`;
}

function hasContactPhone(phoneJsonb?: { number?: string }[] | null): boolean {
  return !!phoneJsonb?.some((entry) => entry.number?.trim());
}

function timelineHasVisitStatus(
  timeline: ContactTimelineItem[],
  status: string,
): boolean {
  return timeline.some((item) => {
    if (item.kind !== "visit") return false;
    if (item.meta === status) return true;
    if (status === "No_Show") {
      return (
        item.meta === "No_Show" ||
        item.body?.includes("[Outcome: No Show]") === true
      );
    }
    return false;
  });
}

function clampLeadSummary(text: string, maxLen = 140): string {
  if (text.length <= maxLen) return text;
  return `${text.slice(0, maxLen - 3).trimEnd()}...`;
}

export function buildLeadSummary(
  input: LeadAssistInput,
  now = Date.now(),
): string {
  const { contact, timeline = [] } = input;
  const parts: string[] = [formatLeadSourceLabel(contact.lead_source)];

  const budget = formatBudgetBand(contact.budget_min, contact.budget_max);
  if (budget) parts.push(`budget ${budget}`);

  const latest = getLatestMeaningfulTimelineItem(timeline);
  if (latest) {
    parts.push(`last: ${formatTimelineSnippet(latest, now)}`);
  }

  if (isLeadUntouched(contact)) {
    const sla = getLeadSlaLevel(contact, now);
    if (sla === "overdue") parts.push("SLA overdue");
    else if (sla === "warn") parts.push("needs first touch");
    else parts.push("untouched");
  }

  return clampLeadSummary(parts.join(" · "));
}

/** True while the inbound WhatsApp auto-qualify bot is mid-conversation. */
export function isContactQualifying(
  contact: Pick<Contact, "qualify_progress">,
): boolean {
  const status = contact.qualify_progress?.status;
  return (
    status === "awaiting_budget" ||
    status === "awaiting_bhk" ||
    status === "awaiting_locality"
  );
}

/** Campaign broadcasts share the whatsapp_messages table but are not 1:1 replies. */
export function isCampaignWhatsAppMessage(
  msg: { status?: string | null; message_body?: string | null } | undefined,
): boolean {
  if (!msg) return false;
  return (
    msg.status === "campaign" || !!msg.message_body?.startsWith("[Campaign]")
  );
}

export function hasPendingInboundWhatsApp(
  timeline: ContactTimelineItem[] | undefined,
): boolean {
  if (!timeline?.length) return false;
  const sorted = sortTimelineItemsDesc(timeline);
  const lastInbound = sorted.find(
    (item) => item.kind === "whatsapp" && item.meta === "INBOUND",
  );
  if (!lastInbound) return false;
  if (lastInbound.status === "unread") return true;
  // Fallback: inbound more recent than any outbound means the agent hasn't replied yet.
  const lastOutbound = sorted.find(
    (item) => item.kind === "whatsapp" && item.meta === "OUTBOUND",
  );
  if (!lastOutbound) return true;
  return (
    new Date(lastInbound.at).getTime() > new Date(lastOutbound.at).getTime()
  );
}

export function suggestNextAction(input: LeadAssistInput): LeadNextAction {
  const {
    contact,
    timeline,
    needsWhatsAppReply,
    hasCompletedVisit,
    hasUpcomingVisit,
  } = input;

  const pendingWa =
    needsWhatsAppReply === true ||
    (needsWhatsAppReply !== false && hasPendingInboundWhatsApp(timeline));

  if (pendingWa) {
    return {
      action: "whatsapp",
      label: "Reply on WhatsApp",
      reason: "Inbound WhatsApp message is waiting for an agent reply.",
    };
  }

  if (isLeadUntouched(contact)) {
    return {
      action: "call",
      label: "First call now",
      reason: "Lead is still untouched; first-touch SLA applies.",
    };
  }

  const noVisitYet = !hasCompletedVisit && !hasUpcomingVisit;
  if (noVisitYet) {
    return {
      action: "schedule_visit",
      label: "Schedule site visit",
      reason: "Lead has been contacted but no site visit is booked yet.",
    };
  }

  if (hasUpcomingVisit) {
    return {
      action: "none",
      label: "Prep for visit",
      reason:
        "A site visit is already scheduled; prepare confirmation and materials.",
    };
  }

  if (hasContactPhone(contact.phone_jsonb)) {
    return {
      action: "whatsapp",
      label: "WhatsApp follow-up",
      reason: "Re-engage on WhatsApp while the lead is still warm.",
    };
  }

  return {
    action: "call",
    label: "Call follow-up",
    reason: "Follow up by phone to keep momentum after the visit.",
  };
}

export function computeLeadScore(
  input: LeadAssistInput,
  now = Date.now(),
): LeadScore {
  const {
    contact,
    timeline = [],
    needsWhatsAppReply,
    hasCompletedVisit,
    hasUpcomingVisit,
  } = input;
  const factors: string[] = [];
  let score = 0;

  const lastSeenIso = contact.last_seen || contact.first_seen;
  if (lastSeenIso) {
    const hours = (now - new Date(lastSeenIso).getTime()) / 3_600_000;
    if (!Number.isNaN(hours) && hours >= 0) {
      if (hours <= 24) {
        score += 25;
        factors.push("Active within 24h");
      } else if (hours <= 24 * 7) {
        score += 15;
        factors.push("Seen within 7 days");
      } else if (hours <= 24 * 30) {
        score += 5;
        factors.push("Seen within 30 days");
      }
    }
  }

  const completed =
    !!hasCompletedVisit || timelineHasVisitStatus(timeline, "Completed");
  const upcoming =
    !!hasUpcomingVisit || timelineHasVisitStatus(timeline, "Scheduled");
  const noShow = timelineHasVisitStatus(timeline, "No_Show");

  if (completed) {
    score += 25;
    factors.push("Completed site visit");
  } else if (upcoming) {
    score += 10;
    factors.push("Visit scheduled");
  }
  if (noShow) {
    score -= 10;
    factors.push("Recent no-show");
  }

  if (contact.budget_min != null || contact.budget_max != null) {
    score += 15;
    factors.push("Budget on file");
  }
  if (contact.preferred_location || contact.preferred_type) {
    score += 10;
    factors.push("Preferences captured");
  }

  if (isLeadUntouched(contact)) {
    score -= 20;
    factors.push("Still untouched");
  }

  const pendingWa =
    needsWhatsAppReply === true ||
    (needsWhatsAppReply !== false && hasPendingInboundWhatsApp(timeline));
  if (pendingWa) {
    score += 10;
    factors.push("WhatsApp reply pending");
  }

  score = Math.max(0, Math.min(100, score));

  let tier: LeadScoreTier = "cold";
  if (score >= 70) tier = "hot";
  else if (score >= 40) tier = "warm";

  return { score, tier, factors };
}

export function draftWhatsAppReply(input: LeadAssistInput): string {
  const {
    contact,
    lastInboundWhatsAppBody,
    hasCompletedVisit,
    hasUpcomingVisit,
  } = input;
  const firstName = contact.first_name?.trim();
  const greeting = firstName ? `Hi ${firstName}` : "Hi there";

  if (lastInboundWhatsAppBody?.trim()) {
    const question =
      contact.budget_min == null && contact.budget_max == null
        ? "What budget range are you considering?"
        : !hasCompletedVisit && !hasUpcomingVisit
          ? "Would you like to schedule a site visit this week?"
          : "Could you share your preferred configuration or timeline?";
    const draft = `${greeting}, thanks for your message. ${question}`;
    return draft.length <= 300 ? draft : draft.slice(0, 297).trimEnd() + "...";
  }

  if (isLeadUntouched(contact)) {
    const draft = `${greeting}, thanks for your enquiry. Which project or location are you exploring? Happy to share details.`;
    return draft.length <= 300 ? draft : draft.slice(0, 297).trimEnd() + "...";
  }

  const draft = `${greeting}, hope you're doing well. Would you like to visit the site? Share a convenient day and time and we'll confirm.`;
  return draft.length <= 300 ? draft : draft.slice(0, 297).trimEnd() + "...";
}

export function leadScoreBadgeClass(tier: LeadScoreTier): string {
  switch (tier) {
    case "hot":
      return "bg-orange-100 text-orange-900 border-transparent";
    case "cold":
      return "bg-slate-100 text-slate-700 border-transparent";
    default:
      return "bg-amber-100 text-amber-900 border-transparent";
  }
}

type TranslateFn = (key: string, options?: { [key: string]: any }) => string;

export const contactGenderDefaultLabels: Record<string, string> = {
  male: "He/Him",
  female: "She/Her",
  nonbinary: "They/Them",
};

const personalInfoTypeMap: Record<string, string> = {
  Work: "work",
  Home: "home",
  Other: "other",
};

export const contactGender: ContactGender[] = [
  {
    value: "male",
    label: "resources.contacts.inputs.genders.male",
    icon: Mars,
  },
  {
    value: "female",
    label: "resources.contacts.inputs.genders.female",
    icon: Venus,
  },
  {
    value: "nonbinary",
    label: "resources.contacts.inputs.genders.nonbinary",
    icon: NonBinary,
  },
];

export const translateContactGenderLabel = (
  gender: { value: string; label: string },
  translate: TranslateFn,
) =>
  translate(gender.label, {
    _: contactGenderDefaultLabels[gender.value] ?? gender.label,
  });

export const translatePersonalInfoTypeLabel = (
  type: string,
  translate: TranslateFn,
) =>
  translate(
    `resources.contacts.inputs.personal_info_types.${personalInfoTypeMap[type] ?? type.toLowerCase()}`,
    {
      _: type,
    },
  );

/**
 * Folds a long line according to vCard specification (max 75 chars per line)
 * Continuation lines start with a space
 */
function foldLine(line: string): string {
  const maxLength = 75;
  if (line.length <= maxLength) return line;

  const result: string[] = [];
  let currentLine = line.substring(0, maxLength);
  let remaining = line.substring(maxLength);

  result.push(currentLine);

  while (remaining.length > 0) {
    // Continuation lines start with a space and can have 74 more chars
    const chunkSize = maxLength - 1;
    currentLine = " " + remaining.substring(0, chunkSize);
    remaining = remaining.substring(chunkSize);
    result.push(currentLine);
  }

  return result.join("\r\n");
}

/**
 * Converts a contact and their company to vCard 3.0 format
 */
export function exportToVCard(
  contact: Contact,
  company?: Company,
  photoData?: { base64: string; mimeType: string },
): string {
  const lines: string[] = [];

  // vCard header
  lines.push("BEGIN:VCARD");
  lines.push("VERSION:3.0");

  // Name (N: Family Name;Given Name;Additional Names;Honorific Prefixes;Honorific Suffixes)
  lines.push(`N:${contact.last_name};${contact.first_name};;;`);

  // Formatted name
  lines.push(`FN:${contact.first_name} ${contact.last_name}`);

  // Title/Job position
  if (contact.title) {
    lines.push(`TITLE:${contact.title}`);
  }

  // Organization
  if (company?.name) {
    lines.push(`ORG:${company.name}`);
  }

  // Emails
  if (contact.email_jsonb && contact.email_jsonb.length > 0) {
    contact.email_jsonb.forEach((emailObj) => {
      const type = emailObj.type.toUpperCase();
      lines.push(`EMAIL;TYPE=${type}:${emailObj.email}`);
    });
  }

  // Phone numbers
  if (contact.phone_jsonb && contact.phone_jsonb.length > 0) {
    contact.phone_jsonb.forEach((phoneObj) => {
      const type = phoneObj.type.toUpperCase();
      lines.push(`TEL;TYPE=${type}:${phoneObj.number}`);
    });
  }

  // LinkedIn URL
  if (contact.linkedin_url) {
    lines.push(`URL:${contact.linkedin_url}`);
  }

  // Background/Note
  if (contact.background) {
    // Escape newlines and special characters in notes
    const escapedNote = contact.background
      .replace(/\\/g, "\\\\")
      .replace(/\n/g, "\\n")
      .replace(/,/g, "\\,")
      .replace(/;/g, "\\;");
    lines.push(`NOTE:${escapedNote}`);
  }

  // Photo/Avatar - vCard 3.0 format with base64 encoding
  if (photoData) {
    // Extract image type from MIME type (e.g., "image/png" -> "PNG")
    const imageType = photoData.mimeType.split("/")[1]?.toUpperCase() || "PNG";

    // vCard 3.0 format: PHOTO;ENCODING=b;TYPE=PNG:
    const photoLine = `PHOTO;ENCODING=b;TYPE=${imageType}:${photoData.base64}`;
    lines.push(foldLine(photoLine));
  }

  // vCard footer
  lines.push("END:VCARD");

  return lines.join("\r\n");
}
